import {
  type DialDocument,
  type GeneratedNodeAttribute,
  parseDocumentAttributeValue,
  parseVideoAttributeValue,
  serialiseDocumentAttributeValue,
} from "@keydials/api-client";

/** Identifies the node an attribute hangs off, for scoping its file uploads. */
export interface AttributeNodeScope {
  sectionKey: string;
  /** Empty string for a Section-target attribute, which has no segment. */
  segmentKey: string;
  itemKey?: string;
}

/** What a file-backed attribute field (Document/Image/Video) needs to render and edit its value. */
export interface AttributeFilesContext {
  instanceId: string;
  /** Every document loaded for this section; a field picks out its own by attribute key and node. */
  docs: DialDocument[];
  /** Editor-or-above here — attaching a file is a higher bar than writing attribute metadata. */
  canUpload: boolean;
  /** Instance admins may delete the underlying file, not just detach it from the field. */
  canManageVersions: boolean;
  /** False in the admin preview, which has no backend to upload to. */
  enabled: boolean;
  onChanged: () => Promise<void>;
}

/** Why a field that would otherwise be editable isn't. */
export type FileAttributeDisabledReason = "preview" | "no-upload-access";

export interface FileAttributeState {
  /** The ids the value names, in order — including any whose document isn't loaded. */
  documentIds: string[];
  /** The loaded documents for those ids, in the same order. */
  attached: DialDocument[];
  /** How many referenced ids no longer resolve to a listed file — quarantined, or deleted by an admin. */
  unresolved: number;
  /** A Video attribute's external link, when that's how its value is set. */
  link: string | null;
  /** Whether the field may hold more than one file (Document/Image with allowMultiple). */
  multiple: boolean;
  /** Whether the user may attach or remove files here. */
  editable: boolean;
  /** Set when the user may edit attributes but still can't attach a file here. */
  disabledReason: FileAttributeDisabledReason | null;
  /** A single-valued field that already holds a file takes no more until it's cleared. */
  atCapacity: boolean;
}

/**
 * Resolves what a Document/Image/Video field should show and allow.
 *
 * Editing needs Editor-or-above AND a live backend — not merely the attribute-edit gate — because the value
 * is a reference to a real upload. The admin preview runs on a mock backend with uploads off, so it reports
 * `"preview"` rather than offering a control that could only fail.
 */
export function fileAttributeState(
  decl: GeneratedNodeAttribute,
  value: string,
  scope: AttributeNodeScope,
  canEdit: boolean,
  files: AttributeFilesContext | undefined,
): FileAttributeState {
  const isVideo = decl.type === "Video";
  const multiple = !isVideo && decl.allowMultiple === true;
  const video = isVideo ? parseVideoAttributeValue(value) : null;

  const documentIds = isVideo
    ? (video?.kind === "Upload" ? [video.documentId] : [])
    : parseDocumentAttributeValue(value);

  const byId = new Map((files?.docs ?? [])
    .filter((d) => d.attributeKey === decl.key && (d.itemKey ?? undefined) === scope.itemKey)
    .map((d) => [d.id, d] as const));
  const attached = documentIds.map((id) => byId.get(id)).filter((d): d is DialDocument => !!d);

  const live = files?.enabled === true;
  const editable = canEdit && live && files.canUpload;
  const disabledReason: FileAttributeDisabledReason | null = !canEdit || editable
    ? null
    : live ? "no-upload-access" : "preview";

  return {
    documentIds,
    attached,
    unresolved: documentIds.length - attached.length,
    link: video?.kind === "Link" ? video.url : null,
    multiple,
    editable,
    disabledReason,
    // Capacity counts files that actually resolve. A value left pointing at a quarantined (or admin-deleted)
    // file would otherwise wedge a single-valued field: nothing to show, and no room to attach a replacement.
    atCapacity: !multiple && attached.length > 0,
  };
}

/** The ids an edit builds on — resolved files only, so a dangling reference is pruned by any edit. */
const liveIds = (state: FileAttributeState) => state.attached.map((d) => d.id);

/** The value a Document/Image field takes after a new upload — appending only when it holds many. */
export function withAttachedDocument(state: FileAttributeState, documentId: string): string {
  return serialiseDocumentAttributeValue(state.multiple ? [...liveIds(state), documentId] : [documentId]);
}

/** The value a Document/Image field takes after one file is detached; empty clears the attribute. */
export function withoutDocument(state: FileAttributeState, documentId: string): string {
  return serialiseDocumentAttributeValue(liveIds(state).filter((id) => id !== documentId));
}

// ---- Segment Files tab contents (KEY-461) ----
//
// The tab used to drop every attribute-backed file, on the reasoning that they already show under their
// field on the Info tab. Tim's call (decision 4, 2026-09-07): show them here too, chipped as the field's,
// alongside the segment's own evidence and its items' — additive, not a move.

/**
 * Which of a section's loaded documents belong on one action point's Files tab: its own files and its
 * items', evidence and attribute-backed alike. A Section-target attribute's files carry no segment key, so
 * they never match here — that exclusion falls out of the same key check that already scoped the tab to
 * one segment, with nothing extra to say.
 */
export function segmentFilesTabDocs(docs: DialDocument[], segmentKey: string): DialDocument[] {
  return docs.filter((d) => d.segmentKey === segmentKey);
}

/** The declared label for a document's backing attribute, or null when it isn't attribute-backed or the
 * declaration can no longer be found (an attribute removed since the file was uploaded). */
export function attributeLabelFor(
  attributes: readonly GeneratedNodeAttribute[],
  attributeKey: string | null | undefined,
): string | null {
  if (!attributeKey) {
    return null;
  }
  return attributes.find((a) => a.key === attributeKey)?.label ?? null;
}
